import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { Link } from 'react-router-dom'
import FormField from '../../utils/FormField'
import { clearGeneralError, loginAsync } from '../../slices/authSlice';
import { XCircleIcon } from '@heroicons/react/24/solid';

const GOOGLE_ERRORS = {
  google_unavailable: 'Google sign-in is not configured on this environment.',
  google_state: 'Google sign-in expired before it completed. Please try again.',
  google_failed: 'Google sign-in could not be completed. Please try again.',
  domain_not_allowed: 'This Google account is outside the allowed Kings Loot Workspace domains.',
  email_unverified: "Google must verify this account's email address before sign-in.",
  no_account: 'That Google account has no Eadin user. Ask an admin to add it first.',
}

export default function SignIn() {
  const error = null;

  const { generalError } = useSelector((state) => state.auth);

  const [user, setUser] = useState({})
  const [googleEnabled, setGoogleEnabled] = useState(false)
  const googleError = GOOGLE_ERRORS[new URLSearchParams(window.location.search).get('error')]
  const dispatch = useDispatch()
  const onSubmit = async (event) => {
    event.preventDefault()
    dispatch(loginAsync({ user }))
  }
  const onChange = (event) => {
    event.preventDefault()
    const { name, value } = event.target
    // error && error[name] && dispatch(clearError({ field: name }))
    setUser((prevState) => ({ ...prevState, [name]: value }))
  }

  useEffect(() => {
    let active = true
    axios.get('/api/v1/auth/google/status')
      .then(({ data }) => { if (active) setGoogleEnabled(Boolean(data?.enabled)) })
      .catch(() => { if (active) setGoogleEnabled(false) })
    return () => { active = false }
  }, [])

  useEffect(() => {
    return () => {
      if (generalError) {
        dispatch(clearGeneralError())
      }
    }
    // eslint-disable-next-line
  }, [])

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-6 lg:px-8">
      <div className="mt-8 mx-auto w-full max-w-lg">
        <div className="bg-white pb-4 pt-2 shadow rounded-lg px-5 lg:pb-8 lg:pt-4 lg:px-10">
          <img src="/brand/eadin-logo-full.png" alt="Eadin" className="h-14 w-auto mx-auto mb-4" />
          {generalError || googleError ? (
            <div className="rounded-md bg-red-50 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{generalError || googleError}</h3>
                </div>
              </div>
            </div>
          ) : null}
          <form className="space-y-6" onSubmit={onSubmit}>
            <FormField
              label="Email Address"
              field="email"
              error={error && error['email']}
              onChange={onChange}
              autoComplete="username"
              state={user}
            />

            <div>
              <FormField
                label="Password"
                field="password"
                type="password"
                error={error && error['password']}
                onChange={onChange}
                autoComplete="current-password"
                state={user}
              />
            </div>

            <div className="flex items-center justify-between">
              <button
                type="submit"
                className="bg-[#111] hover:bg-black text-white px-4 py-2 rounded-md text-sm"
              >
                Sign in
              </button>
              <Link to="/forgot-password" className="text-sm text-gray-600 hover:text-gray-900 underline">
                Forgot password?
              </Link>
            </div>
          </form>

          {googleEnabled ? (
            <>
              <div className="flex items-center gap-3 my-6">
                <span className="h-px flex-1 bg-gray-200" />
                <span className="text-xs uppercase tracking-wide text-gray-400">or</span>
                <span className="h-px flex-1 bg-gray-200" />
              </div>
              <a
                href="/api/auth/google/start"
                className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.2-2.2H12v4.2h6.6c-.1 1.1-.8 2.8-2.4 3.9l-.1.1 3.6 2.8.2.1c2.3-2.1 3.6-5.2 3.6-8.9z" />
                  <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.8-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5l-.1.1-3.8 2.9C3.4 21.3 7.4 24 12 24z" />
                  <path fill="#FBBC05" d="M5.3 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.6.4-2.4l-.1-.1-3.9-3C.5 8.2 0 10 0 12s.5 3.8 1.3 5.5l4-3.1z" />
                  <path fill="#EA4335" d="M12 4.8c2.2 0 3.7.9 4.5 1.7l3.3-3.2C17.9 1.4 15.2 0 12 0 7.4 0 3.4 2.7 1.3 6.5l4 3.1C6.2 6.7 8.9 4.8 12 4.8z" />
                </svg>
                Sign in with Google
              </a>
              <p className="mt-3 text-center text-xs text-gray-500">
                Use your Kings Loot or Kings Holding Google Workspace account.
              </p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}
